import React, { Suspense } from "react";

// Lazy load heavy components to improve initial bundle size
const CommentSection = React.lazy(
    () => import("@/components/comments/CommentSection"),
);
const SelebrityRankWidget = React.lazy(
    () => import("@/components/SelebrityRankWidget"),
);
const NewsReactionBar = React.lazy(
    () => import("@/components/reactions/NewsReactionBar"),
);

// Loading fallbacks
const CommentSectionSkeleton = () => (
    <div className="space-y-4 animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
        <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
                <div key={i} className="flex space-x-3">
                    <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const SelebrityRankSkeleton = () => (
    <div className="space-y-3 animate-pulse">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                <div className="flex-1">
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mt-1"></div>
                </div>
            </div>
        ))}
    </div>
);

const ReactionBarSkeleton = () => (
    <div className="flex items-center space-x-4 animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
    </div>
);

// Wrapper components with Suspense
export const LazyCommentSection: React.FC<any> = (props) => (
    <Suspense fallback={<CommentSectionSkeleton />}>
        <CommentSection {...props} />
    </Suspense>
);

export const LazySelebrityRankWidget: React.FC<any> = (props) => (
    <Suspense fallback={<SelebrityRankSkeleton />}>
        <SelebrityRankWidget {...props} />
    </Suspense>
);

export const LazyNewsReactionBar: React.FC<any> = (props) => (
    <Suspense fallback={<ReactionBarSkeleton />}>
        <NewsReactionBar {...props} />
    </Suspense>
);

// Hook for lazy loading components based on viewport intersection
export const useLazyComponentLoader = (threshold = 0.1) => {
    const [shouldLoad, setShouldLoad] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShouldLoad(true);
                    observer.disconnect();
                }
            },
            { threshold },
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [threshold]);

    return { ref, shouldLoad };
};

// Progressive loading wrapper
export const ProgressiveLoader: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
    threshold?: number;
    className?: string;
}> = ({ children, fallback, threshold = 0.1, className = "" }) => {
    const { ref, shouldLoad } = useLazyComponentLoader(threshold);

    return (
        <div ref={ref} className={className}>
            {shouldLoad
                ? children
                : fallback || (
                      <div className="h-32 animate-pulse bg-gray-200 dark:bg-gray-700 rounded" />
                  )}
        </div>
    );
};

// Optimized image component with progressive loading
export const ProgressiveImage: React.FC<{
    src: string;
    alt: string;
    className?: string;
    lowQualitySrc?: string;
    onLoad?: () => void;
}> = ({ src, alt, className = "", lowQualitySrc, onLoad }) => {
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [isInView, setIsInView] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1, rootMargin: "50px" },
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, []);

    const handleLoad = () => {
        setIsLoaded(true);
        onLoad?.();
    };

    return (
        <div ref={ref} className={`relative overflow-hidden ${className}`}>
            {/* Low quality placeholder */}
            {lowQualitySrc && !isLoaded && (
                <img
                    src={lowQualitySrc}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover filter blur-sm scale-110"
                    loading="eager"
                />
            )}

            {/* Main image */}
            {isInView && (
                <img
                    src={src}
                    alt={alt}
                    className={`w-full h-full object-cover transition-opacity duration-300 ${
                        isLoaded ? "opacity-100" : "opacity-0"
                    }`}
                    loading="lazy"
                    onLoad={handleLoad}
                />
            )}

            {/* Loading skeleton */}
            {!isLoaded && (
                <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse" />
            )}
        </div>
    );
};

// Bundle analyzer helper (development only)
export const BundleAnalyzer: React.FC = () => {
    React.useEffect(() => {
        // Bundle analysis logging removed for production
    }, []);

    return null;
};
